import { logError } from '@dx-ui/framework-logger';
import { Button, Heading, Text } from '@dx-ui/global-ui-prism';
import { FormError } from '@dx-ui/osc-form';
import { OTPInput } from '@dx-ui/osc-otp-input';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { TwoFactorAuthError } from './2fa.errors';
import { AlertIcon } from './alert-icon';
import { CreateGuest2FaTotpDocument, useCreateGuest2FaTotpMutation } from './gql/queries';
import { useAuthApi2FaResendMutation } from './hooks/use-auth-api-2fa-resend-mutation';

const ONE_MINUTE = 60000;
const TEN_MINUTES = ONE_MINUTE * 10;
const MAX_INPUTS = 6;
const MAX_ATTEMPTS = 5;

const initialCode = '';

export type TwoFactorFormProps = {
  /** Custom code sent headline of component  */
  codeSentHeadline?: string;
  /** Custom code resent headline of component */
  codeResentHeadline?: string;
  /** Custom sub-headline / description for component */
  subHeadlineOrDescription?: string;
  /** Hide Headline for component */
  shouldHideHeadline?: boolean;
  /** The guest's preferred delivery method */
  deliveryMethod: string;
  /** @deprecated - GuestId is included in the authorization header */
  guestId?: number;
  /** Language for query to create and send codes */
  language: string;
  /** Status change handler */
  onStatusChange?: (status: Status | null) => void;
  /** Handler that processes the form */
  onSubmit: (code: string) => Promise<void>;
  /** Display information on how to change the delivery method. */
  showPreferredMethodUpdateText?: boolean;
  /** Callback when a resend is triggered */
  onResend?: VoidFunction;
  /** Callback for successful resends */
  onResendSuccess?: VoidFunction;
  /**
   * Optional host-provided resend handler. When supplied, it is used instead of
   * the built-in client-side resend mutations — allowing server-first hosts
   * (e.g. React Router actions) to perform the authenticated resend on the
   * server. Resolve with `'RESEND_EXCEEDED'` to surface the lockout state, or
   * `'SUCCESS'` for a successful resend. Takes precedence over
   * `shouldUseAuthApiResend`.
   */
  onResendRequest?: () => Promise<{ status: 'SUCCESS' | 'RESEND_EXCEEDED' }>;
  /** Whether to use dx-auth-api instead of CreateGuest2FaTotpMutation for OTP resends. */
  shouldUseAuthApiResend?: boolean;
  /** Show Helpline number when user can’t access the email or phone they provided. */
  shouldShowHelpMessage?: boolean;
};

export type Status = 'MAX_ATTEMPTS_EXCEEDED' | 'TIMED_OUT' | 'CODE_EXPIRED' | 'RESEND_EXCEEDED';

type State = {
  code: string;
  attemptCount: number;
  isSubmitting: boolean;
  isCodeExpired: boolean;
  isTimedOut: boolean;
  canResend: boolean;
  hasResent: boolean;
  resendExceeded: boolean;
};

const initialState: State = {
  code: initialCode,
  attemptCount: 0,
  isSubmitting: false,
  isCodeExpired: false,
  isTimedOut: false,
  canResend: true,
  hasResent: false,
  resendExceeded: false,
};

type Action =
  | { type: 'SET_CODE'; code: string }
  | { type: 'INVALID_CODE' }
  | { type: 'RESEND_SUCCESS' }
  | { type: 'RESEND_EXCEEDED' }
  | { type: 'RESEND_TIMER_EXPIRED' }
  | { type: 'DISABLE_RESEND' }
  | { type: 'SET_SUBMITTING' }
  | { type: 'TIMED_OUT' }
  | { type: 'RESET_FORM' };

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_CODE':
      return {
        ...state,
        code: action.code,
      };
    case 'INVALID_CODE':
      return {
        ...state,
        code: initialCode,
        isCodeExpired: true,
        isSubmitting: false,
        attemptCount: state.attemptCount + 1,
        canResend: true,
      };
    case 'RESEND_SUCCESS':
      return {
        ...state,
        code: initialCode,
        hasResent: true,
        isCodeExpired: false,
        attemptCount: 0,
      };
    case 'RESEND_EXCEEDED':
      return {
        ...state,
        canResend: false,
        resendExceeded: true,
      };
    case 'RESEND_TIMER_EXPIRED':
      return {
        ...state,
        hasResent: false,
        canResend: true,
      };
    case 'DISABLE_RESEND':
      return {
        ...state,
        canResend: false,
      };
    case 'SET_SUBMITTING':
      return {
        ...state,
        isCodeExpired: false,
        isSubmitting: true,
      };
    case 'TIMED_OUT':
      return {
        ...state,
        isTimedOut: true,
      };
    case 'RESET_FORM':
      return {
        ...state,
        code: initialCode,
        isSubmitting: false,
      };
    default:
      return state;
  }
};

const useStatus = ({
  attemptCount,
  isCodeExpired,
  isTimedOut,
  resendExceeded,
}: Pick<
  State,
  'attemptCount' | 'isCodeExpired' | 'isTimedOut' | 'resendExceeded'
>): Status | null => {
  if (isTimedOut) {
    return 'TIMED_OUT';
  }
  if (attemptCount >= MAX_ATTEMPTS) {
    return 'MAX_ATTEMPTS_EXCEEDED';
  }
  if (isCodeExpired) {
    return 'CODE_EXPIRED';
  }
  if (resendExceeded) {
    return 'RESEND_EXCEEDED';
  }
  return null;
};

const AlertText: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className="text--sm mb-2 flex justify-center" aria-live="polite">
    <AlertIcon className="me-2 min-h-4 min-w-[18px] text-surface-warn" />
    {children}
  </div>
);

/**
 * Presents a 2FA form to authenticated users that have a `TOTP required` status upon login.
 */
export const TwoFactorForm: React.FC<React.PropsWithChildren<TwoFactorFormProps>> = ({
  language,
  deliveryMethod,
  children,
  onResend,
  onResendSuccess,
  onResendRequest,
  onSubmit,
  onStatusChange,
  showPreferredMethodUpdateText,
  codeSentHeadline,
  subHeadlineOrDescription,
  codeResentHeadline,
  shouldHideHeadline = false,
  shouldUseAuthApiResend = false,
  shouldShowHelpMessage = false,
}) => {
  const { t, ready } = useTranslation('osc-two-factor-auth');
  const submitRef = React.useRef<HTMLButtonElement>(null);
  const resendMutation = useCreateGuest2FaTotpMutation();
  const authApiResendMutation = useAuthApi2FaResendMutation();
  const [isHostResending, setIsHostResending] = React.useState(false);
  const [hasHostResent, setHasHostResent] = React.useState(false);
  const [
    {
      code,
      attemptCount,
      isSubmitting,
      isCodeExpired,
      isTimedOut,
      canResend,
      hasResent,
      resendExceeded,
    },
    dispatch,
  ] = React.useReducer(reducer, initialState);
  const isLoading =
    isSubmitting || resendMutation.isPending || authApiResendMutation.isPending || isHostResending;
  const isValid = code.length === MAX_INPUTS;
  const status = useStatus({ attemptCount, isTimedOut, isCodeExpired, resendExceeded });
  const id = React.useId();
  const errorId = `input-error-${id}`;
  const codeSentHeading = codeSentHeadline || t('headingCodeSent');
  const codeResendHeading = codeResentHeadline || t('headingCodeResent');

  const setCode = (code: string) => {
    dispatch({ type: 'SET_CODE', code });
  };

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch({ type: 'SET_SUBMITTING' });
    await onSubmit(code).catch((error: unknown) => {
      if (error instanceof TwoFactorAuthError && error.name === 'UNKNOWN_ERROR') {
        return dispatch({ type: 'RESET_FORM' });
      }
      dispatch({ type: 'INVALID_CODE' });
    });
  };

  const handleResend = async () => {
    if (!canResend || isLoading) {
      return;
    }
    if (hasResent) {
      if (canResend) {
        dispatch({ type: 'DISABLE_RESEND' });
      }
      return;
    }

    onResend?.();

    if (onResendRequest) {
      /**
       * Host-provided resend (e.g. routed through a server action). Takes
       * precedence over the built-in client-side mutations so server-first
       * apps can perform the authenticated resend on the server.
       */
      setIsHostResending(true);
      try {
        const { status } = await onResendRequest();
        if (status === 'RESEND_EXCEEDED') {
          dispatch({ type: 'RESEND_EXCEEDED' });
        } else {
          dispatch({ type: 'RESEND_SUCCESS' });
          onResendSuccess?.();
        }
        setHasHostResent(true);
      } catch (error) {
        logError('LOGIN_FORM_OTP', error as Error, 'osc-two-factor-auth');
      } finally {
        setIsHostResending(false);
      }
      return;
    }

    if (shouldUseAuthApiResend) {
      try {
        /**
         * This mutation is used to make the resend request to dx-auth-api instead
         * which accepts a tempLogin webGuestToken whereas CreateGuest2FaTotpMutation requires a user token.
         */
        await authApiResendMutation.mutateAsync(undefined, {
          onSuccess: (data) => {
            if (data.success) {
              dispatch({ type: 'RESEND_SUCCESS' });
              onResendSuccess?.();
            } else if (data.fault === 994) {
              dispatch({ type: 'RESEND_EXCEEDED' });
            }
          },
        });
      } catch (error) {
        logError('LOGIN_FORM_OTP', error as Error, 'osc-two-factor-auth');
      }
    } else {
      /**
       * This mutation should send back 'failure' with an error code of 996 as a successful resend
       * unless there is a lockout with code 994 or the request to Reno fails.
       */
      await resendMutation.mutateAsync([CreateGuest2FaTotpDocument, { language }], {
        onSuccess(data) {
          if (data.createGuest2FATotp?.error?.code === 994) {
            dispatch({ type: 'RESEND_EXCEEDED' });
          } else {
            dispatch({ type: 'RESEND_SUCCESS' });
            onResendSuccess?.();
          }
        },
      });
    }
  };

  useIdleTimer({
    timeout: TEN_MINUTES,
    onIdle() {
      dispatch({ type: 'TIMED_OUT' });
    },
  });

  React.useEffect(() => {
    let timer: number | undefined;
    if (!timer && hasResent) {
      timer = window.setTimeout(() => {
        dispatch({ type: 'RESEND_TIMER_EXPIRED' });
      }, ONE_MINUTE);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [hasResent]);

  React.useEffect(() => {
    onStatusChange?.(status);
  }, [onStatusChange, status]);

  React.useEffect(() => {
    if (isValid) submitRef.current?.focus();
  }, [isValid]);

  if (!ready) {
    return null;
  }

  const isResendSuccess =
    resendMutation.isSuccess || authApiResendMutation.isSuccess || hasHostResent;
  const isResendPending =
    resendMutation.isPending || authApiResendMutation.isPending || isHostResending;

  return (
    <>
      <form method="POST" className="mx-auto max-w-md text-center" onSubmit={onFormSubmit}>
        {!shouldHideHeadline ? (
          <Heading level={2} size="lg" className="mb-2" aria-live="polite">
            {isResendSuccess || (!isResendSuccess && isResendPending)
              ? codeResendHeading
              : codeSentHeading}
          </Heading>
        ) : null}
        <Text className="mb-4 px-6 leading-tight">
          {subHeadlineOrDescription
            ? subHeadlineOrDescription
            : t('codeDelivered', { deliveryMethod })}
        </Text>
        <div className="mb-4 flex w-full justify-center">
          <OTPInput
            code={code}
            onChange={setCode}
            numFields={MAX_INPUTS}
            isError={isCodeExpired}
            isDisabled={isLoading}
          />
        </div>
        {shouldShowHelpMessage ? (
          <div className="mb-4 flex w-full justify-center">
            <Text>{t('helpMessageWhenBlocked')}</Text>
          </div>
        ) : null}
        {isCodeExpired ? (
          <div className="mb-4 flex w-full justify-center">
            <FormError
              id={errorId}
              error={{ type: 'required', message: t('headingCodeExpired') }}
            />
          </div>
        ) : null}

        <div className="mb-4">
          {isLoading ? null : isCodeExpired ? (
            <AlertText>
              {canResend
                ? t('submitAttempts', { count: MAX_ATTEMPTS - attemptCount })
                : t('resendTimeout')}
            </AlertText>
          ) : !resendExceeded && isResendSuccess && !canResend ? (
            <AlertText>{t('resendTimeout')}</AlertText>
          ) : resendExceeded && isResendSuccess ? (
            <AlertText>{t('resendExceeded')}</AlertText>
          ) : null}
          {showPreferredMethodUpdateText ? (
            <Text size="sm" className="mb-6">
              {t('codePreferredMethod')}
            </Text>
          ) : null}
          <div className="mb-2">
            <Button
              fill="solid"
              size="lg"
              className="w-64"
              disabled={!isValid || isLoading}
              ref={submitRef}
              type="submit"
              label={t('submit')}
            />
          </div>
          {!resendExceeded ? (
            <Button
              fill="flat"
              size="sm"
              type="button"
              onClick={handleResend}
              disabled={!canResend || isLoading}
              label={t('resendCode')}
            />
          ) : null}
        </div>
      </form>
      {children}
    </>
  );
};
